<script lang="ts">
import type { Snippet } from 'svelte';
import type { ClassValue, SvelteHTMLElements } from 'svelte/elements';
import clsx from 'clsx';
import { twMerge } from 'tailwind-merge';

import { clickOutside } from '../click-outside';
import {
  type FloatingFlipOptions,
  type FloatingPlacement,
  type FloatingReferenceElement,
  type FloatingShiftOptions,
  type FloatingSizeOptions,
  type FloatingStrategy,
  floatingStyle,
} from './floating-style';

type HTMLDivAttributes = SvelteHTMLElements['div'];

interface Props extends HTMLDivAttributes {
  /** The HTML element the popup should float next to. */
  referenceElement: FloatingReferenceElement | undefined;

  /** The placement of the popup relative to the reference element. */
  placement?: FloatingPlacement | undefined;

  /** The offset of the popup relative to the reference element. */
  offset?: number | undefined;

  /** The flip options for the popup. */
  flip?: FloatingFlipOptions | undefined;

  /** The shift options for the popup. */
  shift?: FloatingShiftOptions | undefined;

  /** The size options for the popup. */
  size?: FloatingSizeOptions | undefined;

  /** The strategy for positioning the popup. */
  strategy?: FloatingStrategy | undefined;

  /** Whether the popup should automatically position itself. */
  auto?: boolean | undefined;

  /** Classes to apply to the pop-up. */
  class?: ClassValue;

  /** Component children. */
  children?: Snippet;

  /** Whether the popup should allow click outside to close. */
  allowClickOutside?: boolean;

  /** Callback for when the user clicks outside the pop-up. */
  onClickOutside?: (target: Element) => unknown;
}

const {
  referenceElement,
  placement,
  offset,
  flip,
  shift,
  size,
  strategy,
  auto,
  class: extraClass,
  children,
  allowClickOutside = false,
  onClickOutside,
  ...otherProps
}: Props = $props();

const style = floatingStyle();
let floatingElement: HTMLElement | undefined = $state();

$effect(() => {
  style.register({
    referenceElement,
    floatingElement,
    placement,
    offset,
    flip,
    shift,
    size,
    strategy,
    auto,
  });
});

const classNames = $derived(
  twMerge(
    // escape hatch for z-index layering (eg open a popup from a floating menu)
    // using z-1000 because z-max is not a default tailwind class
    'z-1000 top-0 left-0 w-max',
    clsx(extraClass)
  )
);

const handleClickOutside = (target: Element) => {
  if (allowClickOutside) {
    return;
  }

  onClickOutside?.(target);
};
</script>

<div
  {...otherProps}
  bind:this={floatingElement}
  class={[
    classNames,
    strategy === 'fixed' ? 'fixed' : 'absolute',
    !$style && 'invisible',
  ]}
  style:top={$style?.top}
  style:left={$style?.left}
  use:clickOutside={handleClickOutside}
>
  {@render children?.()}
</div>
